import { Button, Card, Checkbox, Col, Divider, Form, Input, Row, Space, Tag, Typography } from "antd";
import { default as BusinessAreaSelectFormItem } from "containers/BusinessAreaSelectFormItem";
import { default as DataSetSelectFormItem } from "containers/DataSetSelectFormItem";
import { default as SchemaModelFormItem } from "containers/SchemaModelFormItem";
import { default as SubscriptionSourceDataSetSelectFormItem } from "containers/SubscriptionSourceDataSetSelectFormItem";
import dayjs from "dayjs";
import React, { useCallback, useEffect } from "react";

const DATASET_DEFAULT_ITEM = {
    dataSetId: "ALL_DATASETS",
    dataSetName: "All Datasets under this schema",
};

interface ManageDataSyndicationSubscriptionProps {
    subscription: IDataSyndicationSubscription;
    onConfirmSubscription?: (subscription: IDataSyndicationConfirmSubscription) => void;
    onCancelClick?: () => void;
    isLoading?: boolean;
    formErrors?: Record<string, string>;
    readOnlyView?: boolean;
    permission?: {
        canEdit: boolean;
        canAdd: boolean;
    };
}

const EMPTY_OBJECT = {};

const ManageDataSyndicationSubscription: React.FC<ManageDataSyndicationSubscriptionProps> = ({
    subscription,
    onConfirmSubscription,
    onCancelClick,
    isLoading = false,
    formErrors,
    readOnlyView,
    permission,
}) => {
    const [form] = Form.useForm();
    const [selectedBusinessAreaId, setSelectedBusinessAreaId] = React.useState<string | null>(subscription?.targetBusinessAreaId || null);
    const [selectedSchemaId, setSelectedSchemaId] = React.useState<string | null>(subscription?.targetSchemaId || null);

    const formatDate = useCallback((dateString: string) => {
        return dateString ? dayjs(dateString).format("YYYY-MM-DD HH:mm:ss") : "-";
    }, []);

    useEffect(() => {
        if (subscription) {
            form.setFieldsValue({
                targetBusinessAreaId: subscription.targetBusinessAreaId,
                targetSchemaId: subscription.targetSchemaId,
                sourceDataSetId: subscription.sourceDataSetId === DATASET_DEFAULT_ITEM.dataSetId ? null : subscription.sourceDataSetId,
                targetDataSetId: subscription.targetDataSetId,
                includeExistingRecords: subscription.includeExistingRecords,
            });
        }
    }, [form, subscription]);

    const getStatusTag = useCallback((status: string) => {
        switch (status.toUpperCase()) {
            case "PENDING":
                return <Tag color="orange">Pending</Tag>;
            case "CONFIRMED":
                return <Tag color="green">Confirmed</Tag>;
            default:
                return <Tag>{status}</Tag>;
        }
    }, []);

    const handleConfirm = useCallback(() => {
        form.validateFields()
            .then((values) => {
                if (onConfirmSubscription) {
                    onConfirmSubscription({
                        subscriptionId: subscription.subscriptionId,
                        targetBusinessAreaId: values.targetBusinessAreaId,
                        targetSchemaId: values.targetSchemaId,
                        sourceDataSetId: values.sourceDataSetId,
                        targetDataSetId: values.targetDataSetId,
                        includeExistingRecords: values.includeExistingRecords,
                    });
                }
            })
            .catch((error) => {
                console.error("Form validation failed:", error);
            });
    }, [form, subscription, onConfirmSubscription]);

    const onBusinessAreaChange = React.useCallback(
        (businessAreaId: string, businessArea: any) => {
            setSelectedBusinessAreaId(businessAreaId);
            if (form.getFieldValue("targetBusinessAreaId") !== businessAreaId) {
                form.setFieldsValue({
                    businessAreaDescription: businessArea.description,
                    targetSchemaId: null,
                    targetDataSetId: null,
                });
            } else {
                form.setFieldsValue({
                    businessAreaDescription: businessArea.description,
                });
            }
        },
        [form],
    );

    const onSchemaModelChange = React.useCallback(
        (schemaId: string, schemaModel: any) => {
            setSelectedSchemaId(schemaId);
            if (form.getFieldValue("targetSchemaId") !== schemaId) {
                form.setFieldsValue({
                    schemaDescription: schemaModel.description,
                    targetDataSetId: null,
                });
            } else {
                form.setFieldsValue({
                    schemaDescription: schemaModel.description,
                });
            }
        },
        [form, setSelectedSchemaId],
    );

    const getFormErrors = React.useCallback(
        (fieldName: string) => {
            if (formErrors && formErrors[fieldName]) {
                return {
                    help: formErrors[fieldName],
                    validateStatus: "error",
                };
            }
            return EMPTY_OBJECT;
        },
        [formErrors],
    );

    const isReadOnlyView = React.useCallback(() => {
        return readOnlyView || subscription.status === "CONFIRMED" || !(permission?.canEdit || permission?.canAdd);
    }, [readOnlyView, permission]);

    const onDataSetChange = useCallback((dataSetId: any, dataSet: any) => {}, []);

    return (
        <div style={{ height: "100%", overflow: "auto", padding: "5px" }}>
            <Card
                variant="outlined"
                style={{ marginBottom: 24 }}
                title={
                    <Typography.Title level={5} style={{ marginBottom: 0 }}>
                        Subscription Details
                    </Typography.Title>
                }
            >
                <Form form={form}>
                    <Row gutter={24}>
                        <Col span={12}>
                            <Form.Item labelCol={{ span: 24 }} label={<Typography.Text type="secondary">Name</Typography.Text>}>
                                <Typography.Text strong>{subscription.name}</Typography.Text>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item labelCol={{ span: 24 }} label={<Typography.Text type="secondary">Purpose</Typography.Text>}>
                                <Typography.Text strong>{subscription.description}</Typography.Text>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={24}>
                        <Col span={12}>
                            <Form.Item labelCol={{ span: 24 }} label="Source Business Area">
                                <Typography.Text strong>{subscription.sourceBusinessAreaName}</Typography.Text>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item labelCol={{ span: 24 }} label="Source Schema">
                                <Typography.Text strong>{subscription.sourceSchemaName}</Typography.Text>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={24}>
                        <Col span={12}>
                            <BusinessAreaSelectFormItem
                                form={form}
                                formItemProps={{
                                    labelCol: { span: 24 },
                                    label: "Target Business Area",
                                    name: "targetBusinessAreaId",
                                    rules: [{ required: true, message: "Please select a Business Area" }],
                                    ...getFormErrors("targetBusinessAreaId"),
                                }}
                                disabled={isReadOnlyView()}
                                onChange={onBusinessAreaChange}
                            ></BusinessAreaSelectFormItem>
                        </Col>
                        <Col span={12}>
                            <Form.Item labelCol={{ span: 24 }} name="businessAreaDescription" label="Business Area Description">
                                <Input readOnly={true} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={24}>
                        <Col span={12}>
                            <SchemaModelFormItem
                                form={form}
                                businessAreaId={selectedBusinessAreaId}
                                formItemProps={{
                                    labelCol: { span: 24 },
                                    label: "Target Schema",
                                    name: ["targetSchemaId"],
                                    rules: [{ required: true, message: "Please select a Schema" }],
                                    ...getFormErrors("targetSchemaId"),
                                }}
                                disabled={isReadOnlyView()}
                                onChange={onSchemaModelChange}
                            ></SchemaModelFormItem>
                        </Col>
                        <Col span={12}>
                            <Form.Item labelCol={{ span: 24 }} name="schemaDescription" label="Schema Description">
                                <Input readOnly={true} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={24} style={{ marginBottom: 16 }}>
                        <Col span={12}>
                            <Row gutter={24} style={{ marginBottom: 16 }}>
                                <Col style={{ alignContent: "flex-end" }}>
                                    <Form.Item>
                                        <Checkbox checked={subscription.shareDataset} disabled>
                                            {"Source Dataset Shared"}
                                        </Checkbox>
                                    </Form.Item>
                                </Col>
                                <Col>
                                    <SubscriptionSourceDataSetSelectFormItem
                                        subscriptionId={subscription.subscriptionId}
                                        formItemProps={{
                                            labelCol: { span: 24 },
                                            label: "Source DataSet",
                                            name: ["sourceDataSetId"],
                                            rules: [{ required: true, message: "Please select a source DataSet" }],
                                            ...getFormErrors("sourceDataSetId"),
                                        }}
                                        onChange={onDataSetChange}
                                        disabled={!subscription.shareDataset || isReadOnlyView()}
                                    ></SubscriptionSourceDataSetSelectFormItem>
                                </Col>
                            </Row>
                        </Col>
                        <Col span={12}>
                            <Row gutter={24} style={{ marginBottom: 16, flexDirection: "column" }}>
                                <Col style={{ alignContent: "flex-end" }}>
                                    <DataSetSelectFormItem
                                        businessAreaId={selectedBusinessAreaId}
                                        schemaId={selectedSchemaId}
                                        formItemProps={{
                                            labelCol: { span: 24 },
                                            label: "Target DataSet",
                                            name: ["targetDataSetId"],
                                            rules: [{ required: true, message: "Please select a destination DataSet" }],
                                            ...getFormErrors("targetDataSetId"),
                                        }}
                                        onChange={onDataSetChange}
                                        disabled={isReadOnlyView()}
                                    ></DataSetSelectFormItem>
                                </Col>
                                <Col style={{ alignContent: "flex-end" }}>
                                    <Form.Item name={"includeExistingRecords"} valuePropName="checked">
                                        <Checkbox checked={subscription.includeExistingRecords} disabled={isReadOnlyView()}>
                                            {"Include existing records"}
                                        </Checkbox>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    <Row gutter={24}>
                        <Col span={12}>
                            <Form.Item labelCol={{ span: 24 }} label={<Typography.Text type="secondary">Status</Typography.Text>}>
                                {getStatusTag(subscription.status)}
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item labelCol={{ span: 24 }} label={<Typography.Text type="secondary">Enabled</Typography.Text>}>
                                <Typography.Text strong>{subscription.enabled ? "Yes" : "No"}</Typography.Text>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={24}>
                        <Col span={12}>
                            <Form.Item labelCol={{ span: 24 }} label={<Typography.Text type="secondary">Valid From</Typography.Text>}>
                                <Typography.Text strong>{formatDate(subscription.validFrom)}</Typography.Text>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item labelCol={{ span: 24 }} label={<Typography.Text type="secondary">Valid Until</Typography.Text>}>
                                <Typography.Text strong>{formatDate(subscription.validUntil)}</Typography.Text>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={24}>
                        <Col span={12}>
                            <Form.Item labelCol={{ span: 24 }} label={<Typography.Text type="secondary">Rate Value</Typography.Text>}>
                                <Typography.Text strong>{subscription.rateValue}</Typography.Text>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item labelCol={{ span: 24 }} label={<Typography.Text type="secondary">Rate Unit</Typography.Text>}>
                                <Typography.Text strong>{subscription.rateUnit}</Typography.Text>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Divider style={{ margin: "24px 0" }} />
                    <Row>
                        <Col span={24} style={{ textAlign: "right" }}>
                            <Space>
                                <>
                                    <Button onClick={onCancelClick}>Cancel</Button>
                                    {subscription.status === "PENDING" && (
                                        <Button type="primary" onClick={handleConfirm} loading={isLoading} disabled={isReadOnlyView()}>
                                            Confirm Subscription
                                        </Button>
                                    )}
                                </>
                            </Space>
                        </Col>
                    </Row>
                </Form>
            </Card>
        </div>
    );
};

export default ManageDataSyndicationSubscription;
